import { useState, useEffect } from 'react'
import emailjs from '@emailjs/browser'
import { careersAPI, authAPI, getCurrentUser } from '../services/api'

const EJS_SERVICE   = process.env.REACT_APP_EMAILJS_SERVICE_ID
const EJS_OWNER_TPL = process.env.REACT_APP_EMAILJS_OWNER_TEMPLATE_ID
const EJS_USER_TPL  = process.env.REACT_APP_EMAILJS_TEMPLATE_ID
const EJS_KEY       = process.env.REACT_APP_EMAILJS_PUBLIC_KEY
const CONTACT_EMAIL = 'info@groupeouambo.com'

const STATUS_COLORS = {
  pending:   { bg: '#fef9c3', color: '#854d0e', border: '#fef08a' },
  reviewing: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  accepted:  { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  rejected:  { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
}

const S = {
  page: { fontFamily: "'Inter','Segoe UI',sans-serif", color: '#0f172a', minHeight: '100vh' },
  hero: { background: 'linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)', color: 'white', padding: '72px 24px 56px', textAlign: 'center' },
  heroEyebrow: { fontSize: '0.8rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.75, marginBottom: 12 },
  heroTitle: { fontSize: 'clamp(2rem,5vw,3rem)', fontWeight: 900, margin: '0 0 16px' },
  heroSub: { fontSize: '1.05rem', opacity: 0.85, maxWidth: 560, margin: '0 auto 32px' },
  tabs: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 },
  tab: (active) => ({ padding: '10px 24px', borderRadius: 999, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', border: 'none', background: active ? 'white' : 'rgba(255,255,255,.15)', color: active ? '#1e3a5f' : 'white', transition: 'all .2s' }),
  body: { maxWidth: 1000, margin: '0 auto', padding: '40px 20px 80px' },
  sectionTitle: { fontSize: '1.4rem', fontWeight: 800, marginBottom: 24, color: '#0f172a' },
  jobCard: (active) => ({ background: 'white', border: `2px solid ${active ? '#2563eb' : '#e5e7eb'}`, borderRadius: 14, padding: '24px 28px', marginBottom: 16, cursor: 'pointer', transition: 'border-color .2s, box-shadow .2s', boxShadow: active ? '0 0 0 4px #dbeafe' : '0 1px 4px rgba(0,0,0,.06)' }),
  jobTitle: { fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' },
  jobMeta: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  pill: (color) => ({ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: color + '18', color, border: `1px solid ${color}44` }),
  jobDesc: { fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6, margin: 0 },
  applyBtn: { background: '#2563eb', color: 'white', border: 'none', padding: '10px 22px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', marginTop: 16 },
  emptyBox: { textAlign: 'center', padding: '60px 20px', color: '#94a3b8' },
  authCard: { background: 'white', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,.1)', padding: '36px 32px', maxWidth: 440, margin: '0 auto' },
  authTitle: { fontSize: '1.4rem', fontWeight: 800, marginBottom: 6 },
  authSub: { color: '#6b7280', fontSize: '0.9rem', marginBottom: 28 },
  authTabs: { display: 'flex', borderBottom: '2px solid #f1f5f9', marginBottom: 24 },
  authTab: (active) => ({ padding: '8px 20px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', border: 'none', background: 'none', color: active ? '#2563eb' : '#94a3b8', borderBottom: active ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -2 }),
  formGroup: { marginBottom: 16 },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 6 },
  input: { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', minHeight: 120, resize: 'vertical' },
  submitBtn: { width: '100%', background: '#1e3a5f', color: 'white', border: 'none', padding: '12px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', marginTop: 8 },
  errorBox: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: '0.875rem', marginBottom: 16 },
  successBox: { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', fontSize: '0.875rem', marginBottom: 16 },
  infoBox: { background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 8, padding: '14px 18px', fontSize: '0.9rem', marginBottom: 20, lineHeight: 1.6 },
  applyCard: { background: 'white', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,.1)', padding: '36px 32px', maxWidth: 600, margin: '0 auto' },
  statusCard: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,.05)' },
  statusBadge: (status) => { const c = STATUS_COLORS[status] || STATUS_COLORS.pending; return { display: 'inline-block', padding: '3px 12px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700, background: c.bg, color: c.color, border: `1px solid ${c.border}` }; },
  logoutBtn: { background: 'none', border: '1px solid #e2e8f0', color: '#64748b', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 },
  userBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 20px', marginBottom: 28 },
  detailBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 20px', marginTop: 12, fontSize: '0.88rem', lineHeight: 1.7, color: '#374151' },
}

export default function CareersPage({ t, lang = 'en' }) {
  const c = t?.careers || {}
  const [tab, setTab] = useState('jobs')
  const [jobs, setJobs] = useState([])
  const [selectedJob, setSelectedJob] = useState(null)
  const [loadingJobs, setLoadingJobs] = useState(true)

  const [user, setUser] = useState(() => getCurrentUser())
  const [authMode, setAuthMode] = useState('login')
  const [authForm, setAuthForm] = useState({ username: '', email: '', password: '' })
  const [authError, setAuthError] = useState('')
  const [authLoading, setAuthLoading] = useState(false)

  const [coverLetter, setCoverLetter] = useState('')
  const [resumeFile, setResumeFile] = useState(null)
  const [applyError, setApplyError] = useState('')
  const [applySuccess, setApplySuccess] = useState(false)
  const [applyLoading, setApplyLoading] = useState(false)

  const [myApps, setMyApps] = useState([])
  const [appsLoading, setAppsLoading] = useState(false)
  const [expandedJob, setExpandedJob] = useState(null)

  const statusLabel = (status) => {
    const map = {
      pending: c.statusPending,
      reviewing: c.statusReviewing,
      accepted: c.statusAccepted,
      rejected: c.statusRejected,
    }
    return map[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : '')
  }

  useEffect(() => {
    careersAPI.getJobs().then(setJobs).catch(() => {}).finally(() => setLoadingJobs(false))
  }, [])

  useEffect(() => {
    if (user && tab === 'account') loadMyApps()
  }, [user, tab])

  const loadMyApps = async () => {
    setAppsLoading(true)
    try { setMyApps(await careersAPI.getMyApplications()) } catch {}
    setAppsLoading(false)
  }

  const handleAuth = async (e) => {
    e.preventDefault()
    setAuthError('')
    setAuthLoading(true)
    try {
      if (authMode === 'login') {
        const data = await authAPI.login(authForm.email, authForm.password)
        setUser(data.user)
        setTab('account')
      } else {
        await authAPI.register(authForm.username, authForm.email, authForm.password)
        const data = await authAPI.login(authForm.email, authForm.password)
        setUser(data.user)
        setTab('account')
      }
    } catch (err) {
      setAuthError(err.message)
    }
    setAuthLoading(false)
  }

  const handleLogout = () => {
    authAPI.logout()
    setUser(null)
    setMyApps([])
    setTab('jobs')
  }

  const handleApply = async (e) => {
    e.preventDefault()
    if (!user) { setTab('auth'); return }
    setApplyError('')
    setApplyLoading(true)
    try {
      await careersAPI.apply({ job_id: selectedJob?.id || null, cover_letter: coverLetter, resume: resumeFile })

      emailjs.send(EJS_SERVICE, EJS_OWNER_TPL, {
        subject: `New Job Application — ${user.username || user.email} for ${selectedJob?.title || 'General'}`,
        email: user.email,
        content: `Position: ${selectedJob?.title || 'General Application'}\nDepartment: ${selectedJob?.department || '—'}\nApplicant: ${user.username || user.email}\nEmail: ${user.email}\n\nCover Letter:\n${coverLetter || '(none)'}`,
        to_email: CONTACT_EMAIL,
      }, EJS_KEY).catch(() => {})

      emailjs.send(EJS_SERVICE, EJS_USER_TPL, {
        subject: `Application received — ${selectedJob?.title || 'Groupe Ouambo'}`,
        email: user.email,
        content: `Dear ${user.username || user.email},\n\nThank you for applying for the position of ${selectedJob?.title || 'a role'} at Groupe Ouambo. We have received your application and will review it shortly.\n\nWe will be in touch regarding next steps.\n\nBest regards,\nGroupe Ouambo HR Team`,
      }, EJS_KEY).catch(() => {})

      setApplySuccess(true)
      setCoverLetter('')
      setResumeFile(null)
      setSelectedJob(null)
      setTimeout(() => { setTab('account'); loadMyApps() }, 2000)
    } catch (err) {
      setApplyError(err.message)
    }
    setApplyLoading(false)
  }

  return (
    <div style={S.page}>
      <div style={S.hero}>
        <p style={S.heroEyebrow}>{c.heroEyebrow}</p>
        <h1 style={S.heroTitle}>{c.heroTitle}</h1>
        <p style={S.heroSub}>{c.heroSub}</p>
        <div style={S.tabs}>
          <button style={S.tab(tab === 'jobs')} onClick={() => setTab('jobs')}>{c.openPositions}</button>
          {user
            ? <>
                <button style={S.tab(tab === 'apply')} onClick={() => setTab('apply')}>{c.applyNow}</button>
                <button style={S.tab(tab === 'account')} onClick={() => setTab('account')}>{c.myApplications}</button>
              </>
            : <button style={S.tab(tab === 'auth')} onClick={() => setTab('auth')}>{c.signInRegister}</button>
          }
        </div>
      </div>

      <div style={S.body}>
        {tab === 'jobs' && (
          <>
            <h2 style={S.sectionTitle}>{c.openPositions}</h2>
            {loadingJobs && <p style={{ color: '#94a3b8' }}>{c.loadingPositions}</p>}
            {!loadingJobs && jobs.length === 0 && (
              <div style={S.emptyBox}>
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
                <p style={{ fontWeight: 600, marginBottom: 4 }}>{c.noPositionsTitle}</p>
                <p style={{ fontSize: '0.88rem' }}>{c.noPositionsBody}</p>
                <button style={{ ...S.applyBtn, marginTop: 20 }} onClick={() => user ? setTab('apply') : setTab('auth')}>
                  {user ? c.sendGeneral : c.signInToApply}
                </button>
              </div>
            )}
            {jobs.map(job => (
              <div key={job.id} style={S.jobCard(expandedJob === job.id)} onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                <h3 style={S.jobTitle}>{job.title}</h3>
                <div style={S.jobMeta}>
                  {job.department && <span style={S.pill('#1e3a5f')}>{job.department}</span>}
                  {job.location && <span style={S.pill('#7c3aed')}>{job.location}</span>}
                  {job.type && <span style={S.pill('#059669')}>{job.type}</span>}
                </div>
                <p style={S.jobDesc}>{job.description?.slice(0, 160)}{job.description?.length > 160 ? '…' : ''}</p>
                {expandedJob === job.id && (
                  <div style={S.detailBox}>
                    {job.description && <><strong>{c.description}</strong><br />{job.description}<br /><br /></>}
                    {job.requirements && <><strong>{c.requirements}</strong><br />{job.requirements}</>}
                    <div style={{ marginTop: 20 }}>
                      <button style={S.applyBtn} onClick={(e) => {
                        e.stopPropagation()
                        setSelectedJob(job)
                        user ? setTab('apply') : setTab('auth')
                      }}>
                        {user ? c.applyForPosition : c.signInToApply}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === 'apply' && (
          <div style={S.applyCard}>
            <h2 style={{ ...S.sectionTitle, marginBottom: 6 }}>
              {selectedJob ? `${c.applyPrefix} ${selectedJob.title}` : c.generalApplication}
            </h2>

            {!user ? (
              <div>
                <div style={S.infoBox}>{c.needAccount}</div>
                <button style={S.submitBtn} onClick={() => setTab('auth')}>{c.signInCreate}</button>
              </div>
            ) : applySuccess ? (
              <div style={S.successBox}>
                ✅ {c.applicationSubmitted} {user.email}. {c.willBeInTouch}
              </div>
            ) : (
              <>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: '0.88rem', color: '#166534' }}>
                  {c.applyingAs} <strong>{user.username || user.email}</strong> ({user.email})
                </div>

                {selectedJob && (
                  <p style={{ color: '#6b7280', fontSize: '0.88rem', marginBottom: 20 }}>
                    {selectedJob.department} · {selectedJob.location} · {selectedJob.type}
                    <button onClick={() => setSelectedJob(null)} style={{ marginLeft: 10, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.82rem' }}>✕ {c.clear}</button>
                  </p>
                )}

                {!selectedJob && jobs.length > 0 && (
                  <div style={S.formGroup}>
                    <label style={S.label}>{c.selectPosition}</label>
                    <select style={S.input} onChange={e => {
                      const j = jobs.find(j => String(j.id) === e.target.value)
                      setSelectedJob(j || null)
                    }} value={selectedJob?.id || ''}>
                      <option value="">{c.generalSpontaneous}</option>
                      {jobs.map(j => <option key={j.id} value={j.id}>{j.title} — {j.department}</option>)}
                    </select>
                  </div>
                )}

                <form onSubmit={handleApply}>
                  {applyError && <div style={S.errorBox}>{applyError}</div>}
                  <div style={S.formGroup}>
                    <label style={S.label}>{c.coverLetter} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{c.optional}</span></label>
                    <textarea style={S.textarea} value={coverLetter}
                      onChange={e => setCoverLetter(e.target.value)}
                      placeholder={c.coverPlaceholder} />
                  </div>
                  <div style={S.formGroup}>
                    <label style={S.label}>{c.resume} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{c.resumeHint}</span></label>
                    <input type="file" accept=".pdf,.doc,.docx" style={S.input}
                      onChange={e => setResumeFile(e.target.files?.[0] || null)} />
                  </div>
                  <button style={S.submitBtn} type="submit" disabled={applyLoading}>
                    {applyLoading ? c.submitting : c.submitApplication}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {tab === 'auth' && !user && (
          <div style={S.authCard}>
            <h2 style={S.authTitle}>{c.applicantAccount}</h2>
            <p style={S.authSub}>{c.authSub}</p>
            <div style={S.authTabs}>
              <button style={S.authTab(authMode === 'login')} onClick={() => { setAuthMode('login'); setAuthError('') }}>{c.signIn}</button>
              <button style={S.authTab(authMode === 'register')} onClick={() => { setAuthMode('register'); setAuthError('') }}>{c.createAccount}</button>
            </div>
            <form onSubmit={handleAuth}>
              {authError && <div style={S.errorBox}>{authError}</div>}
              {authMode === 'register' && (
                <div style={S.formGroup}>
                  <label style={S.label}>{c.fullName}</label>
                  <input style={S.input} value={authForm.username} onChange={e => setAuthForm(f => ({ ...f, username: e.target.value }))} required placeholder={c.namePlaceholder} />
                </div>
              )}
              <div style={S.formGroup}>
                <label style={S.label}>{c.emailAddress}</label>
                <input style={S.input} type="email" value={authForm.email} onChange={e => setAuthForm(f => ({ ...f, email: e.target.value }))} required placeholder="your@email.com" />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>{c.password}</label>
                <input style={S.input} type="password" value={authForm.password} onChange={e => setAuthForm(f => ({ ...f, password: e.target.value }))} required placeholder="••••••••" />
              </div>
              <button style={S.submitBtn} type="submit" disabled={authLoading}>
                {authLoading ? c.pleaseWait : authMode === 'login' ? c.signIn : c.createAccount}
              </button>
            </form>
          </div>
        )}

        {tab === 'account' && user && (
          <>
            <div style={S.userBar}>
              <div>
                <strong>{user.username || user.email}</strong>
                <span style={{ marginLeft: 10, fontSize: '0.8rem', color: '#64748b' }}>{user.email}</span>
              </div>
              <button style={S.logoutBtn} onClick={handleLogout}>{c.signOut}</button>
            </div>

            <h2 style={S.sectionTitle}>{c.myApplications}</h2>
            <button style={{ ...S.applyBtn, marginBottom: 24, display: 'inline-block' }} onClick={() => setTab('apply')}>
              {c.submitNew}
            </button>

            {appsLoading && <p style={{ color: '#94a3b8' }}>{c.loading}</p>}
            {!appsLoading && myApps.length === 0 && (
              <div style={S.emptyBox}>
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📂</div>
                <p style={{ fontWeight: 600 }}>{c.noAppsTitle}</p>
                <p style={{ fontSize: '0.88rem' }}>{c.noAppsBody}</p>
              </div>
            )}
            {myApps.map(app => (
              <div key={app.id} style={S.statusCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700 }}>
                      {app.job_title || c.generalApplication}
                    </h3>
                    {app.department && <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>{app.department}</p>}
                  </div>
                  <span style={S.statusBadge(app.status)}>
                    {statusLabel(app.status)}
                  </span>
                </div>
                <div style={{ marginTop: 10, fontSize: '0.82rem', color: '#94a3b8' }}>
                  {c.applied}: {new Date(app.applied_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}
                </div>
                {app.notes && (
                  <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem', color: '#374151' }}>
                    <strong>{c.noteFromHr}:</strong> {app.notes}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === 'account' && !user && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ color: '#64748b', marginBottom: 16 }}>{c.pleaseSignInApps}</p>
            <button style={S.applyBtn} onClick={() => setTab('auth')}>{c.signInRegister}</button>
          </div>
        )}
      </div>
    </div>
  )
}
